import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function TermsOfService() {
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <header className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/4c54787fd2047152d108467e5113439e/e5fe30b4f26c08cfb369c5b19d957793.png"
              alt="Sri Maruthi Teeth Empire Logo"
              className="w-10 h-10 object-contain rounded-lg"
            />
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
              Sri Maruthi Teeth Empire
            </h1>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-sm text-gray-300 hover:text-teal-400 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
        </div>
      </header>

      {/* Hero Banner */}
      <section className="bg-gradient-to-r from-teal-600 to-teal-500 text-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-2xl mx-auto mb-5">
            <i className="ri-file-list-3-line text-3xl"></i>
          </div>
          <h1 className="text-4xl font-bold mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
            Terms of Service
          </h1>
          <p className="text-teal-100 text-sm">Last updated: January 2025</p>
        </div>
      </section>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <article className="space-y-10">
          {/* Introduction */}
          <section>
            <p className="text-gray-600 text-sm leading-relaxed">
              Welcome to <strong>Sri Maruthi Teeth Empire</strong>. These Terms of Service (&quot;Terms&quot;) govern your use of our website and the dental services provided by Sri Maruthi Teeth Empire Dental Clinic, located at Botanical Garden Road, Kondapur, Hyderabad, Telangana. By accessing or using our website, you agree to be bound by these Terms. If you do not agree with any part of these Terms, please do not use our website or services.
            </p>
          </section>

          {/* Acceptance of Terms */}
          <section>
            <h4
              className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-3"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <a href="#acceptance" id="acceptance" className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-teal-50 text-teal-600 rounded-lg text-sm font-bold">
                  1
                </span>
                Acceptance of Terms
              </a>
            </h4>
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="text-gray-600 text-sm leading-relaxed">
                By using the Sri Maruthi Teeth Empire website, booking an appointment, or availing any of our dental services in Kondapur, Hyderabad, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and our <Link to="/privacy-policy" className="text-teal-600 hover:text-teal-700 underline cursor-pointer">Privacy Policy</Link>. These Terms apply to all visitors, patients, and users of our website and services.
              </p>
            </div>
          </section>

          {/* Dental Services */}
          <section>
            <h4
              className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-3"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <a href="#services" id="services" className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-teal-50 text-teal-600 rounded-lg text-sm font-bold">
                  2
                </span>
                Dental Services
              </a>
            </h4>
            <div className="bg-gray-50 rounded-xl p-6 space-y-4">
              <p className="text-gray-600 text-sm leading-relaxed">
                Sri Maruthi Teeth Empire provides a range of professional dental care services including but not limited to:
              </p>
              <ul className="space-y-1.5 text-gray-600 text-sm">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-500 mt-0.5"></i>
                  General dentistry, preventive care, and routine check-ups
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-500 mt-0.5"></i>
                  Cosmetic dentistry including teeth whitening and veneers
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-500 mt-0.5"></i>
                  Orthodontic treatments and dental aligners
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-500 mt-0.5"></i>
                  Root canal therapy and endodontic procedures
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-500 mt-0.5"></i>
                  Dental implants and prosthetic restorations
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-500 mt-0.5"></i>
                  Pediatric dentistry and emergency dental care
                </li>
              </ul>
              <p className="text-gray-600 text-sm leading-relaxed">
                All dental treatments are performed by qualified and licensed dental professionals. Treatment plans are customized based on individual patient needs and clinical assessments conducted at our Kondapur clinic.
              </p>
            </div>
          </section>

          {/* Appointment Booking */}
          <section>
            <h4
              className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-3"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <a href="#appointments" id="appointments" className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-teal-50 text-teal-600 rounded-lg text-sm font-bold">
                  3
                </span>
                Appointment Booking &amp; Cancellation
              </a>
            </h4>
            <div className="bg-gray-50 rounded-xl p-6 space-y-4">
              <div>
                <h5 className="text-sm font-semibold text-slate-800 mb-2">Booking</h5>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Appointments can be booked through our website, by calling <a href="tel:+918340978930" className="text-teal-600 hover:text-teal-700 cursor-pointer">+91 83409 78930</a>, or via <a href="https://wa.me/918340978930" target="_blank" rel="nofollow noopener noreferrer" className="text-teal-600 hover:text-teal-700 cursor-pointer">WhatsApp</a>. Booking an appointment does not guarantee a specific treatment outcome. All appointments are subject to availability and confirmation by our clinic staff.
                </p>
              </div>
              <div>
                <h5 className="text-sm font-semibold text-slate-800 mb-2">Cancellation &amp; Rescheduling</h5>
                <p className="text-gray-600 text-sm leading-relaxed">
                  We kindly request that you provide at least <strong>24 hours&apos; notice</strong> if you need to cancel or reschedule your appointment. Repeated no-shows or late cancellations may result in restrictions on future online booking privileges. Emergency cancellations will be handled on a case-by-case basis.
                </p>
              </div>
              <div>
                <h5 className="text-sm font-semibold text-slate-800 mb-2">Punctuality</h5>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Please arrive at least 10 minutes before your scheduled appointment time. Late arrivals may result in shortened appointment times or rescheduling to accommodate other patients.
                </p>
              </div>
            </div>
          </section>

          {/* Patient Responsibilities */}
          <section>
            <h4
              className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-3"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <a href="#patient-responsibilities" id="patient-responsibilities" className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-teal-50 text-teal-600 rounded-lg text-sm font-bold">
                  4
                </span>
                Patient Responsibilities
              </a>
            </h4>
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="text-gray-600 text-sm leading-relaxed mb-3">
                As a patient of Sri Maruthi Teeth Empire, you agree to:
              </p>
              <ul className="space-y-2.5 text-gray-600 text-sm">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 flex items-center justify-center bg-teal-100 text-teal-600 rounded-full text-xs font-bold flex-shrink-0 mt-0.5">a</span>
                  <span>Provide accurate and complete personal and medical information, including any allergies, medications, or pre-existing conditions</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 flex items-center justify-center bg-teal-100 text-teal-600 rounded-full text-xs font-bold flex-shrink-0 mt-0.5">b</span>
                  <span>Inform our dental team of any changes to your health status before treatment</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 flex items-center justify-center bg-teal-100 text-teal-600 rounded-full text-xs font-bold flex-shrink-0 mt-0.5">c</span>
                  <span>Follow post-treatment care instructions provided by our dental professionals</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 flex items-center justify-center bg-teal-100 text-teal-600 rounded-full text-xs font-bold flex-shrink-0 mt-0.5">d</span>
                  <span>Make timely payments for services rendered as per the agreed treatment plan</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 flex items-center justify-center bg-teal-100 text-teal-600 rounded-full text-xs font-bold flex-shrink-0 mt-0.5">e</span>
                  <span>Treat clinic staff and other patients with respect and courtesy</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Payment Terms */}
          <section>
            <h4
              className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-3"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <a href="#payment" id="payment" className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-teal-50 text-teal-600 rounded-lg text-sm font-bold">
                  5
                </span>
                Payment Terms
              </a>
            </h4>
            <div className="bg-gray-50 rounded-xl p-6 space-y-3">
              <p className="text-gray-600 text-sm leading-relaxed">
                Payment for dental services is due at the time of treatment unless otherwise agreed upon. We accept various payment methods including cash, credit/debit cards, and UPI payments. A detailed cost estimate will be provided before any treatment begins.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed">
                For extensive treatment plans, we may offer flexible payment options. Please discuss payment arrangements with our front desk team. Sri Maruthi Teeth Empire reserves the right to modify pricing for services, and any changes will be communicated to patients prior to treatment.
              </p>
            </div>
          </section>

          {/* Website Use */}
          <section>
            <h4
              className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-3"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <a href="#website-use" id="website-use" className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-teal-50 text-teal-600 rounded-lg text-sm font-bold">
                  6
                </span>
                Website Use
              </a>
            </h4>
            <div className="bg-gray-50 rounded-xl p-6 space-y-3">
              <p className="text-gray-600 text-sm leading-relaxed">
                The content on the Sri Maruthi Teeth Empire website, including text, images, graphics, and other materials, is provided for informational purposes only and does not constitute medical advice. Always consult with a qualified dental professional for diagnosis and treatment recommendations.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed">
                You agree not to:
              </p>
              <ul className="space-y-1.5 text-gray-600 text-sm">
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-line text-red-400 mt-0.5"></i>
                  Use the website for any unlawful or unauthorized purpose
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-line text-red-400 mt-0.5"></i>
                  Attempt to gain unauthorized access to any part of the website
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-line text-red-400 mt-0.5"></i>
                  Reproduce, distribute, or modify any content without prior written consent
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-line text-red-400 mt-0.5"></i>
                  Submit false or misleading information through any forms on the website
                </li>
              </ul>
            </div>
          </section>

          {/* Intellectual Property */}
          <section>
            <h4
              className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-3"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <a href="#intellectual-property" id="intellectual-property" className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-teal-50 text-teal-600 rounded-lg text-sm font-bold">
                  7
                </span>
                Intellectual Property
              </a>
            </h4>
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="text-gray-600 text-sm leading-relaxed">
                All content on this website, including but not limited to the Sri Maruthi Teeth Empire name, logo, text, images, graphics, design elements, and software, is the property of Sri Maruthi Teeth Empire and is protected by applicable intellectual property laws. Unauthorized use, reproduction, or distribution of any content is strictly prohibited without prior written permission from Sri Maruthi Teeth Empire.
              </p>
            </div>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h4
              className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-3"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <a href="#liability" id="liability" className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-teal-50 text-teal-600 rounded-lg text-sm font-bold">
                  8
                </span>
                Limitation of Liability
              </a>
            </h4>
            <div className="bg-gray-50 rounded-xl p-6 space-y-3">
              <p className="text-gray-600 text-sm leading-relaxed">
                Sri Maruthi Teeth Empire strives to provide the highest quality dental care. However, to the fullest extent permitted by law:
              </p>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li className="flex items-start gap-2">
                  <i className="ri-information-line text-teal-500 mt-0.5"></i>
                  <span>We do not guarantee specific treatment outcomes, as results may vary based on individual patient conditions</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-information-line text-teal-500 mt-0.5"></i>
                  <span>We are not liable for any indirect, incidental, or consequential damages arising from the use of our website or services</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-information-line text-teal-500 mt-0.5"></i>
                  <span>The website is provided &quot;as is&quot; without warranties of any kind, either express or implied</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Third-Party Links */}
          <section>
            <h4
              className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-3"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <a href="#third-party" id="third-party" className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-teal-50 text-teal-600 rounded-lg text-sm font-bold">
                  9
                </span>
                Third-Party Links
              </a>
            </h4>
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="text-gray-600 text-sm leading-relaxed">
                Our website may contain links to third-party websites such as Google Maps, WhatsApp, and social media platforms. These links are provided for your convenience only. Sri Maruthi Teeth Empire does not endorse, control, or assume responsibility for the content, privacy policies, or practices of any third-party websites. We encourage you to review the terms and policies of any external sites you visit.
              </p>
            </div>
          </section>

          {/* Governing Law */}
          <section>
            <h4
              className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-3"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <a href="#governing-law" id="governing-law" className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-teal-50 text-teal-600 rounded-lg text-sm font-bold">
                  10
                </span>
                Governing Law
              </a>
            </h4>
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="text-gray-600 text-sm leading-relaxed">
                These Terms of Service shall be governed by and construed in accordance with the laws of India. Any disputes arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts in Hyderabad, Telangana, India.
              </p>
            </div>
          </section>

          {/* Changes to Terms */}
          <section>
            <h4
              className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-3"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <a href="#changes" id="changes" className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-teal-50 text-teal-600 rounded-lg text-sm font-bold">
                  11
                </span>
                Changes to These Terms
              </a>
            </h4>
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="text-gray-600 text-sm leading-relaxed">
                Sri Maruthi Teeth Empire reserves the right to update or modify these Terms of Service at any time without prior notice. Changes will be effective immediately upon posting on this page with an updated &quot;Last updated&quot; date. Your continued use of our website and services after any changes constitutes acceptance of the revised Terms. We encourage you to review this page periodically.
              </p>
            </div>
          </section>

          {/* Contact Us */}
          <section>
            <h4
              className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-3"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <a href="#contact-us" id="contact-us" className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-teal-50 text-teal-600 rounded-lg text-sm font-bold">
                  12
                </span>
                Contact Us
              </a>
            </h4>
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-100">
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                If you have any questions or concerns about these Terms of Service, please contact us:
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center bg-teal-100 text-teal-600 rounded-lg">
                    <i className="ri-building-2-line text-sm"></i>
                  </span>
                  <span className="text-gray-700 text-sm font-medium">Sri Maruthi Teeth Empire Dental Clinic</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center bg-teal-100 text-teal-600 rounded-lg">
                    <i className="ri-map-pin-line text-sm"></i>
                  </span>
                  <span className="text-gray-700 text-sm">
                    Botanical Garden Road, Kondapur, Hyderabad, Telangana 500084
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center bg-teal-100 text-teal-600 rounded-lg">
                    <i className="ri-mail-line text-sm"></i>
                  </span>
                  <a
                    href="mailto:teethempire08@gmail.com"
                    className="text-teal-600 hover:text-teal-700 text-sm font-medium transition-colors cursor-pointer"
                  >
                    teethempire08@gmail.com
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center bg-teal-100 text-teal-600 rounded-lg">
                    <i className="ri-phone-line text-sm"></i>
                  </span>
                  <a
                    href="tel:+918340978930"
                    className="text-teal-600 hover:text-teal-700 text-sm font-medium transition-colors cursor-pointer"
                  >
                    +91 83409 78930
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center bg-green-100 text-green-600 rounded-lg">
                    <i className="ri-whatsapp-line text-sm"></i>
                  </span>
                  <a
                    href="https://wa.me/918340978930"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="text-green-600 hover:text-green-700 text-sm font-medium transition-colors cursor-pointer"
                  >
                    WhatsApp: +91 83409 78930
                  </a>
                </div>
              </div>
            </div>
          </section>
        </article>
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-400 text-sm">&copy; 2025 Sri Maruthi Teeth Empire. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <Link
              to="/"
              className="text-gray-400 hover:text-teal-400 text-sm transition-colors cursor-pointer"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
